/**
 * Transition planning (X11-X13).
 *
 * Pure arithmetic over numbers and beat arrays — no audio nodes — so the
 * alignment maths can be proved correct under node before anything is heard.
 * A misaligned transition is obvious by ear but very hard to debug by ear.
 */

export type TransitionMethod = "beat-aligned" | "tempo-ramp" | "loop-roll" | "crossfade";

export interface PlannerConfig {
  /** Transition length in bars, where a grid exists (X11a). */
  transitionBars: number;
  /** Fallback length in seconds, where it does not. */
  transitionSeconds: number;
  beatsPerBar: number;
  /** Tempo fader travel as a fraction; alignment is refused beyond it. */
  tempoRange: number;
  /** Longest a transition may run, whatever the bar maths says. */
  maxTransitionSeconds: number;
  /** Widest gap bridgeable by bending the outgoing deck (X13a). */
  rampMaxDeviation: number;
  /** Bars over which that bend happens. */
  rampBars: number;
  /** Loop-roll slice lengths in beats, played back to back (X13b). */
  loopRollBeats: readonly number[];
  /** Shorter roll used when skipping out of a track on demand. */
  urgentRollBeats: readonly number[];
  /** Longest an aggressive skip may take, in bars. */
  urgentBars: number;
  /**
   * Blend length range for transitions involving a track with no tempo.
   * Ambient material wants a longer, less eventful blend than a rhythmic one,
   * and wants it to vary so repeated transitions do not feel mechanical.
   */
  ambientBlendMin: number;
  ambientBlendMax: number;
}

export const DEFAULT_PLANNER_CONFIG: PlannerConfig = {
  transitionBars: 8,
  transitionSeconds: 12,
  beatsPerBar: 4,
  tempoRange: 0.16,
  maxTransitionSeconds: 45,
  rampMaxDeviation: 0.25,
  rampBars: 4,
  // Two bars, then halving into a stutter: the shape of a roll that reads as
  // deliberate rather than as a glitch.
  loopRollBeats: [4, 4, 2, 2, 1, 1, 0.5, 0.5, 0.5, 0.5],
  // About a bar and a half: long enough to read as a gesture, short enough
  // that "get me out of this track" is honoured promptly.
  urgentRollBeats: [2, 1, 1, 0.5, 0.5, 0.25, 0.25],
  urgentBars: 2,
  ambientBlendMin: 12,
  ambientBlendMax: 30,
};

export interface DeckSide {
  /** Beat positions in track time, unscaled by rate. */
  beats: readonly number[];
  /** Effective BPM at rate 1. */
  tempo: number | null;
  /**
   * The region of the track being played, in track time. For a long track this
   * is a chosen segment rather than the whole thing (see chooseSegment).
   */
  start: number;
  end: number;
}

export interface OutgoingSide extends DeckSide {
  /** Current playback position in track time at `now`. */
  position: number;
  /** Current playback rate multiplier. */
  rate: number;
}

export interface PlanInput {
  /** AudioContext.currentTime when planning. */
  now: number;
  outgoing: OutgoingSide;
  incoming: DeckSide;
  config: PlannerConfig;
  /** Injectable so blend-length variation is reproducible under test. */
  random?: () => number;
  /**
   * "scheduled" transitions at the end of the segment, as normal. The others
   * are operator-driven skips: leave this track now, either gracefully or
   * within a couple of bars.
   */
  urgency?: "scheduled" | "smooth" | "aggressive";
}

export interface RollSlice {
  /** Audio-clock time to play this repeat. */
  at: number;
  /** Offset into the outgoing track. */
  offset: number;
  /** Wall-clock length of the repeat. */
  duration: number;
}

export interface TransitionPlan {
  method: TransitionMethod;
  reason: string;
  /** Audio-clock time at which the incoming deck starts and the fade begins. */
  startAt: number;
  duration: number;
  /** Offset into the incoming track to start from. */
  incomingOffset: number;
  /** Total playback multiplier for the incoming deck. */
  incomingRate: number;
  /**
   * Present for "tempo-ramp". Describes only the bend; the blend that follows
   * is re-planned once the decks match (X13c).
   */
  ramp?: { targetRate: number; startAt: number; duration: number };
  /** Present for "loop-roll". Each repeat is scheduled separately (X13b). */
  roll?: { slices: RollSlice[]; endsAt: number };
}

/** Convert a track-time position on the outgoing deck to an audio-clock time. */
function toClock(input: PlanInput, trackTime: number): number {
  const { now, outgoing } = input;
  return now + (trackTime - outgoing.position) / outgoing.rate;
}

/**
 * Rate needed to pull `incoming` onto `reference`, considering half and double
 * time. Returns null when nothing lands inside the fader range.
 */
export function matchRate(
  incomingTempo: number,
  referenceBpm: number,
  tempoRange: number,
): number | null {
  let best: number | null = null;
  for (const octave of [0.5, 1, 2]) {
    const rate = referenceBpm / (incomingTempo * octave);
    const deviation = Math.abs(rate - 1);
    if (deviation <= tempoRange && (best === null || Math.abs(rate - 1) < Math.abs(best - 1))) {
      best = rate;
    }
  }
  return best;
}

/**
 * Rate the outgoing deck must reach for the incoming track to play at unity.
 * Unlike matchRate this is not bounded by the fader, because a ramp is
 * automated rather than performed on the fader.
 */
export function rampTargetRate(
  outgoingTempo: number,
  incomingTempo: number,
  currentRate: number,
): number {
  let best = incomingTempo / outgoingTempo;
  for (const octave of [0.5, 2]) {
    const candidate = (incomingTempo * octave) / outgoingTempo;
    if (Math.abs(candidate - currentRate) < Math.abs(best - currentRate)) best = candidate;
  }
  return best;
}

function medianInterval(beats: readonly number[]): number {
  if (beats.length < 2) return 0;
  const gaps: number[] = [];
  for (let i = 1; i < beats.length; i++) gaps.push((beats[i] as number) - (beats[i - 1] as number));
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] as number;
}

function nearestBeatAtOrAfter(beats: readonly number[], time: number): number | null {
  for (const beat of beats) {
    if (beat >= time) return beat;
  }
  return null;
}

/** The next bar line at or after `time`, for transitions that cannot wait. */
function nextBarAtOrAfter(
  beats: readonly number[],
  time: number,
  beatsPerBar: number,
): { time: number; index: number } | null {
  for (let i = 0; i < beats.length; i += beatsPerBar) {
    const beat = beats[i] as number;
    if (beat >= time) return { time: beat, index: i };
  }
  return null;
}

/**
 * Choose the outgoing beat closest to `target`, preferring bar lines.
 *
 * Starting a transition on a bar rather than an arbitrary beat is what makes it
 * sound intentional; the phase of "bar" here is arbitrary but consistent within
 * a track, which is enough for the two grids to lock.
 */
function anchorBeat(
  beats: readonly number[],
  target: number,
  beatsPerBar: number,
): { time: number; index: number } | null {
  if (beats.length === 0) return null;

  let bestIndex = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < beats.length; i++) {
    if (i % beatsPerBar !== 0) continue;
    const distance = Math.abs((beats[i] as number) - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  if (bestIndex === -1) return null;
  return { time: beats[bestIndex] as number, index: bestIndex };
}

/**
 * Build the scheduled repeats of a loop roll (X13b).
 *
 * Each repeat replays the same segment head for a shortening number of beats,
 * so the pattern accelerates into a stutter. They are emitted as discrete
 * scheduled slices rather than a looping node, because shortening a real loop
 * needs JavaScript at every bar and a backgrounded tab cannot promise that.
 */
export function loopRollSlices(
  loopStart: number,
  beatInterval: number,
  pattern: readonly number[],
  rate: number,
  startAt: number,
): { slices: RollSlice[]; endsAt: number } {
  const slices: RollSlice[] = [];
  let at = startAt;
  for (const beats of pattern) {
    const duration = (beats * beatInterval) / rate;
    if (duration <= 0) continue;
    slices.push({ at, offset: loopStart, duration });
    at += duration;
  }
  return { slices, endsAt: at };
}

export function planTransition(input: PlanInput): TransitionPlan {
  const { now, outgoing, incoming, config } = input;
  const random = input.random ?? Math.random;
  const urgency = input.urgency ?? "scheduled";
  const immediate = urgency !== "scheduled";

  const outgoingBpm = outgoing.tempo === null ? null : outgoing.tempo * outgoing.rate;
  const ambient = outgoing.tempo === null || incoming.tempo === null;

  // Length in bars where there is a grid to count them against, else seconds.
  // Ambient transitions get their own, longer and deliberately varying range.
  let duration: number;
  if (ambient) {
    const spread = Math.max(0, config.ambientBlendMax - config.ambientBlendMin);
    duration = config.ambientBlendMin + random() * spread;
  } else if (outgoingBpm !== null && outgoingBpm > 0) {
    duration = (config.transitionBars * config.beatsPerBar * 60) / outgoingBpm;
  } else {
    duration = config.transitionSeconds;
  }
  duration = Math.min(duration, config.maxTransitionSeconds);

  if (urgency === "aggressive") {
    // A skip that takes a normal-length blend is not a skip.
    const bars =
      outgoingBpm && outgoingBpm > 0
        ? (config.urgentBars * config.beatsPerBar * 60) / outgoingBpm
        : 1.5;
    duration = Math.min(duration, bars);
  }

  // Wall-clock time the outgoing track has left before the end of its segment.
  const remaining = (outgoing.end - outgoing.position) / outgoing.rate;
  // Never plan a fade longer than what is actually left to fade out of.
  duration = Math.max(1, Math.min(duration, Math.max(1, remaining - 0.25)));

  // Kept in track time as well as clock time. The beat arrays are in track
  // time, and mixing the two units silently anchors the transition to the last
  // beat of the track instead of the intended one.
  // A scheduled transition lands at the end of the segment; an operator skip
  // starts as soon as the music allows.
  const desiredStartTrack = immediate
    ? outgoing.position + 0.2 * outgoing.rate
    : outgoing.end - duration * outgoing.rate;
  const desiredStart = Math.max(now + 0.1, toClock(input, desiredStartTrack));

  const crossfade = (reason: string): TransitionPlan => ({
    method: "crossfade",
    reason,
    startAt: desiredStart,
    duration,
    incomingOffset: incoming.start,
    incomingRate: 1,
  });

  if (outgoing.tempo === null) return crossfade("outgoing track has no steady tempo");
  if (incoming.tempo === null) return crossfade("incoming track has no steady tempo");
  if (outgoingBpm === null || outgoingBpm <= 0) return crossfade("outgoing tempo unusable");
  if (incoming.beats.length < 2) return crossfade("incoming track has no beat grid");
  if (outgoing.beats.length < 2) return crossfade("outgoing track has no beat grid");

  // Start on the first incoming beat at or after its segment start, so the
  // incoming track enters on a beat rather than mid-phrase.
  const incomingBeat = nearestBeatAtOrAfter(incoming.beats, incoming.start);
  if (incomingBeat === null) return crossfade("incoming grid does not reach its start");

  const rate = matchRate(incoming.tempo, outgoingBpm, config.tempoRange);

  if (urgency === "aggressive") {
    const urgent = urgentRoll(input, desiredStartTrack, incomingBeat);
    if (urgent) return urgent;
    return {
      ...crossfade("skipping out of this track"),
      startAt: desiredStart,
      duration: Math.min(duration, 1.5),
    };
  }

  if (rate !== null) {
    const anchor = immediate
      ? nextBarAtOrAfter(outgoing.beats, desiredStartTrack, config.beatsPerBar)
      : anchorBeat(outgoing.beats, desiredStartTrack, config.beatsPerBar);
    if (anchor === null) return crossfade("no outgoing bar line to anchor to");

    const anchoredStart = toClock(input, anchor.time);
    if (anchoredStart < now + 0.05) return crossfade("nearest bar line has already passed");

    return {
      method: "beat-aligned",
      reason:
        `matched ${incoming.tempo.toFixed(1)} to ${outgoingBpm.toFixed(1)} BPM ` +
        `(${(rate * 100 - 100).toFixed(1)}%)`,
      startAt: anchoredStart,
      duration,
      incomingOffset: incomingBeat,
      incomingRate: rate,
    };
  }

  // Beyond the fader, so bridge the gap rather than give up (X13a).
  const target = rampTargetRate(outgoing.tempo, incoming.tempo, outgoing.rate);
  const deviation = Math.abs(target / outgoing.rate - 1);
  const rampDuration = (config.rampBars * config.beatsPerBar * 60) / outgoingBpm;

  // A bend takes bars to complete, so it is never the answer to "leave this
  // track now" - the operator would press the button and hear nothing change.
  if (!immediate && deviation <= config.rampMaxDeviation) {
    // Bend the outgoing deck so the two tempos meet, ending before the blend
    // would have begun. The blend itself is re-planned afterwards (X13c).
    const rampStart = desiredStart - rampDuration;
    if (rampStart > now + 0.05) {
      return {
        method: "tempo-ramp",
        reason:
          `bending ${outgoingBpm.toFixed(1)} to ${(outgoing.tempo * target).toFixed(1)} BPM ` +
          `over ${config.rampBars} bars`,
        startAt: rampStart,
        duration: rampDuration,
        incomingOffset: incomingBeat,
        incomingRate: 1,
        ramp: { targetRate: target, startAt: rampStart, duration: rampDuration },
      };
    }
  }

  // Too far to bend, or too late to start bending: roll the outgoing out and
  // cut in on the downbeat.
  const beatInterval = medianInterval(outgoing.beats);
  const pattern = config.loopRollBeats;
  const rollBeats = pattern.reduce((sum, beats) => sum + beats, 0);
  const rollDuration = (rollBeats * beatInterval) / outgoing.rate;

  // A scheduled roll sits at the end of the segment; a skip starts at the next
  // bar. Anchoring a skip to the end of the track made "cut now" wait out the
  // rest of the track before doing anything.
  const rollTarget = immediate
    ? desiredStartTrack
    : outgoing.end - rollDuration * outgoing.rate;
  const rollAnchor = immediate
    ? nextBarAtOrAfter(outgoing.beats, rollTarget, config.beatsPerBar)
    : anchorBeat(outgoing.beats, rollTarget, config.beatsPerBar);

  if (beatInterval > 0 && rollAnchor !== null) {
    const rollStart = toClock(input, rollAnchor.time);
    if (rollStart > now + 0.05) {
      const roll = loopRollSlices(
        rollAnchor.time,
        beatInterval,
        pattern,
        outgoing.rate,
        rollStart,
      );
      return {
        method: "loop-roll",
        reason:
          `tempo gap too wide to bend (${incoming.tempo.toFixed(1)} against ` +
          `${outgoingBpm.toFixed(1)} BPM) — rolling out and cutting in`,
        // The incoming track starts as the roll ends; the "fade" is a cut.
        startAt: roll.endsAt,
        duration: 0.05,
        incomingOffset: incomingBeat,
        incomingRate: 1,
        roll,
      };
    }
  }

  return crossfade(
    `tempo gap too wide (${incoming.tempo.toFixed(1)} against ${outgoingBpm.toFixed(1)} BPM)`,
  );
}

/**
 * A short roll starting at the next bar line, for an on-demand skip.
 */
function urgentRoll(
  input: PlanInput,
  fromTrackTime: number,
  incomingBeat: number,
): TransitionPlan | null {
  const { outgoing, config, now } = input;
  const beatInterval = medianInterval(outgoing.beats);
  const anchor = nextBarAtOrAfter(outgoing.beats, fromTrackTime, config.beatsPerBar);
  if (beatInterval <= 0 || anchor === null) return null;

  const rollStart = toClock(input, anchor.time);
  if (rollStart < now + 0.05) return null;

  const roll = loopRollSlices(
    anchor.time,
    beatInterval,
    config.urgentRollBeats,
    outgoing.rate,
    rollStart,
  );
  return {
    method: "loop-roll",
    reason: "skipping out of this track",
    startAt: roll.endsAt,
    duration: 0.05,
    incomingOffset: incomingBeat,
    incomingRate: 1,
    roll,
  };
}

/**
 * Where a beat of each deck falls on the audio clock, given a plan. Used by the
 * tests to assert that alignment actually holds, rather than trusting the
 * arithmetic above by inspection.
 */
export function beatClockTimes(
  input: PlanInput,
  plan: TransitionPlan,
  count: number,
): { outgoing: number[]; incoming: number[] } {
  const outgoing: number[] = [];
  for (const beat of input.outgoing.beats) {
    const clock = toClock(input, beat);
    if (clock >= plan.startAt - 1e-9 && outgoing.length < count) outgoing.push(clock);
  }

  const incoming: number[] = [];
  for (const beat of input.incoming.beats) {
    if (beat < plan.incomingOffset - 1e-9) continue;
    const clock = plan.startAt + (beat - plan.incomingOffset) / plan.incomingRate;
    if (incoming.length < count) incoming.push(clock);
  }
  return { outgoing, incoming };
}
